/**
 * TimelineContextProvider
 *
 * Provides shared timeline state and basic computed values to all child components.
 *
 * Architecture:
 * 1. Creates reducer with initial state (expandedIds, hoverData, zoom, etc.)
 * 2. Receives basic computed values via value prop (timelineDateRange, totalWeeks, etc.)
 * 3. Computes weekWidth from reducer state
 * 4. Publishes three contexts:
 *    - TimelineStaticContext: stable layout/config fields (changes on zoom, expand, strategy)
 *    - TimelineInteractionContext: hot-path hover fields (changes on every mousemove)
 *    - TimelineContext: full merged value (compat shim for gradual migration)
 *
 * Advanced computations (dimensions, zoom handlers) are done in CropTimelineInner
 * and provided via TimelineComputedProvider.
 */

import { useReducer, useMemo } from 'react'
import { initialState, TimelineContextReducer } from './timeline-context-reducer'
import { TimelineContextDispatch } from './timeline-context'
import { TimelineStaticContext } from './timeline-static-context'
import { TimelineInteractionContext } from './timeline-interaction-context'
import { WEEK_CONTAINER_FALLBACK_WIDTH } from '../timeline-constants'

export const TimelineContextProvider = ({ children, value = {}, initialStateOverride = {} }) => {
  const [state, dispatch] = useReducer(TimelineContextReducer, { ...initialState, ...initialStateOverride })

  const weekWidth = state.weekContainerWidth || WEEK_CONTAINER_FALLBACK_WIDTH

  // Static context — all non-hover fields. Does NOT change on mousemove/hover events.
  const staticValue = useMemo(() => ({
    expandedIds: state.expandedIds,
    activePhaseKey: state.activePhaseKey,
    activeTaskKey: state.activeTaskKey,
    weekContainerWidth: state.weekContainerWidth,
    groupStrategy: state.groupStrategy,
    sortStrategy: state.sortStrategy,
    containerWidth: state.containerWidth,
    viewStrategy: state.viewStrategy,
    showSuccessions: state.showSuccessions,
    expandedSuccessionKey: state.expandedSuccessionKey,
    showOrgTasks: state.showOrgTasks,
    ...value,
    weekWidth,
  }), [
    state.expandedIds,
    state.activePhaseKey,
    state.activeTaskKey,
    state.weekContainerWidth,
    state.groupStrategy,
    state.sortStrategy,
    state.containerWidth,
    state.viewStrategy,
    state.showSuccessions,
    state.expandedSuccessionKey,
    state.showOrgTasks,
    value,
    weekWidth,
  ])

  // Interaction context — only the 4 hot-path hover fields. Changes on every mousemove.
  const interactionValue = useMemo(() => ({
    hoverData: state.hoverData,
    hoveredPhaseKey: state.hoveredPhaseKey,
    hoveredTaskKey: state.hoveredTaskKey,
    hoveredReportIndex: state.hoveredReportIndex,
  }), [state.hoverData, state.hoveredPhaseKey, state.hoveredTaskKey, state.hoveredReportIndex])

  return (
    <TimelineStaticContext.Provider value={staticValue}>
      <TimelineInteractionContext.Provider value={interactionValue}>
        <TimelineContextDispatch.Provider value={dispatch}>
          {children}
        </TimelineContextDispatch.Provider>
      </TimelineInteractionContext.Provider>
    </TimelineStaticContext.Provider>
  )
}
